// SYNCED WITH veltric/website-builder-frontend/src/lib/utils/datetime-tz.ts — update both repos when changing the math.

/**
 * Timezone-aware datetime helpers.
 *
 * The brand DatePicker / TableCellContentDateTime previously mixed UTC and
 * browser-local semantics: input was treated as UTC, display rerendered the
 * stored UTC instant in the browser's local TZ. This module gives us the two
 * primitives that bridge the two worlds:
 *
 *   - extractWallParts(iso, tz)  → wall-clock parts in that tz
 *   - wallPartsToIso(parts, tz)  → UTC ISO for those wall parts in that tz
 *
 * The slotting helpers (toMinutesFromMidnight / localMinutesToIso /
 * localTimeToIso / formatDate) used to live in src/lib/slotting/utils.ts; they
 * moved here so non-slotting callers can reuse them. slotting/utils re-exports.
 */

export type WallParts = {
  year: number;
  /** 0-indexed, like JS Date.getMonth() */
  month: number;
  day: number;
  hour: number;
  minute: number;
};

const BROWSER_TZ = (): string =>
  Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * `Intl.DateTimeFormat` construction is ~10-50x costlier than `.format()`, and
 * these helpers are called once per appointment per render in hot paths (the
 * slotting calendar funnels every appointment through toMinutesFromMidnight
 * for overlap layout, buffer zones, tiles, and the day filter). Cache formatters
 * by (locale + options) so we build each unique formatter once. The set of
 * (locale, tz, options) combos in use is tiny and bounded, so the Map never
 * grows unbounded.
 */
const _fmtCache = new Map<string, Intl.DateTimeFormat>();
function cachedFormatter(
  locale: string,
  options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
  const key = `${locale}|${JSON.stringify(options)}`;
  let fmt = _fmtCache.get(key);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat(locale, options);
    _fmtCache.set(key, fmt);
  }
  return fmt;
}

/**
 * Wall-clock minutes-from-midnight that the given UTC instant displays at, in
 * the target timezone.
 */
export function toMinutesFromMidnight(
  isoString: string,
  timezone: string,
): number {
  const date = new Date(isoString);
  const formatter = cachedFormatter("en-US", {
    timeZone: timezone,
    hour: "numeric",
    minute: "numeric",
    hour12: false,
  });
  const parts = formatter.formatToParts(date);
  const rawHour = parseInt(parts.find((p) => p.type === "hour")?.value ?? "0");
  const minute = parseInt(
    parts.find((p) => p.type === "minute")?.value ?? "0",
  );
  const hour = rawHour === 24 ? 0 : rawHour;
  return hour * 60 + minute;
}

export function formatTime(minutes: number): string {
  const h = Math.floor(minutes / 60)
    .toString()
    .padStart(2, "0");
  const m = (minutes % 60).toString().padStart(2, "0");
  return `${h}:${m}`;
}

/**
 * The zone's UTC offset in minutes at the given instant, read off the wall
 * clock it shows there: wall parts as if they were UTC, minus the instant.
 * DATE-AWARE, which is the point — a minutes-from-midnight comparison cannot
 * tell "13 h ahead" from "11 h behind".
 */
function offsetMinutesAt(ms: number, timezone: string): number {
  const w = extractWallParts(new Date(ms).toISOString(), timezone);
  return Math.round((wallPartsAsUtcMs(w) - ms) / 60_000);
}

/**
 * Wall-clock parts read as if they were a UTC instant. Not `Date.UTC`, which
 * maps a year 0–99 to 1900–1999 and would put a first-century wall time 1900
 * years off.
 */
function wallPartsAsUtcMs(p: WallParts): number {
  const d = new Date(0);
  d.setUTCFullYear(p.year, p.month, p.day);
  d.setUTCHours(p.hour, p.minute, 0, 0);
  return d.getTime();
}

/**
 * Convert visual minutes (wall-clock minutes from midnight in `timezone`) + a
 * calendar date ("YYYY-MM-DD") into a UTC ISO string. Inverse of
 * `toMinutesFromMidnight`.
 *
 * The wall time is read as if it were UTC, then shifted by the zone's offset
 * at that instant; when the shift crosses a DST switch the offset on the far
 * side is tried once more and kept only if it agrees with itself. Offsets are
 * read date-aware (`offsetMinutesAt`), so that is exact for every real wall
 * time at every offset — including ±12, +13 and +14 (Auckland, Chatham,
 * Tongatapu, Fakaofo, Kiritimati, `Etc/GMT+12`), where the former minute-of-
 * day correction wrapped by a whole day — and for the hours right before and
 * after a DST switch, where it was off by one hour (PB-TZ-1).
 *
 * Non-existent wall times (the spring-forward gap, e.g. 02:30 in Berlin on
 * the night the clock jumps 02:00 → 03:00) have no instant; they resolve to
 * the wall time minus the offset in force at the instant the wall time reads
 * as UTC — the instant the former correction returned too, kept on purpose so
 * nothing already stored moves (except on the gap nights where that one was
 * a whole day off, at ±12 h: those now land one shift away like everywhere
 * else). Ambiguous wall times (the fall-back overlap) resolve to the same
 * side as before, for the same reason. WHICH side follows from where the
 * switch falls against the wall time read as UTC, not from the zone being
 * east or west: the skipped 02:30 is 01:30 CET in Berlin but 03:30 EDT in New
 * York; the repeated 02:30 in Berlin is its second (CET) occurrence, the
 * repeated 01:30 in New York its first (EDT).
 */
export function localMinutesToIso(
  date: string,
  minutes: number,
  timezone: string,
): string {
  // 1440 = end-of-day midnight → treat as 00:00 of next day
  if (minutes >= 1440) {
    const nextDay = new Date(`${date}T00:00:00Z`);
    nextDay.setUTCDate(nextDay.getUTCDate() + 1);
    const nd = nextDay.toISOString().slice(0, 10);
    return localMinutesToIso(nd, minutes - 1440, timezone);
  }
  minutes = Math.max(0, Math.min(1439, minutes));
  const h = Math.floor(minutes / 60)
    .toString()
    .padStart(2, "0");
  const m = (minutes % 60).toString().padStart(2, "0");
  // The wall time read as if it were UTC — parsed from the ISO string, so a
  // four-digit year stays itself and a date that does not parse is NaN, which
  // throws RangeError below exactly as the shipped body did. The synced
  // wb-frontend copy builds it the same way.
  const wall = Date.parse(`${date}T${h}:${m}:00Z`);
  const o1 = offsetMinutesAt(wall, timezone);
  const t1 = wall - o1 * 60_000;
  const o2 = offsetMinutesAt(t1, timezone);
  let instant = t1;
  if (o2 !== o1) {
    // Across a DST switch: the offset at the candidate differs from the one
    // the wall time was read with. The second candidate wins only when it
    // round-trips; otherwise the wall time does not exist (a gap) and the
    // first one stands.
    const t2 = wall - o2 * 60_000;
    if (offsetMinutesAt(t2, timezone) === o2) instant = t2;
  }
  return new Date(instant).toISOString().replace(/\.\d{3}Z$/, "Z");
}

export function localTimeToIso(
  date: string,
  time: string,
  timezone: string,
): string {
  const parts = time.split(":");
  const minutes = parseInt(parts[0] ?? "0") * 60 + parseInt(parts[1] ?? "0");
  return localMinutesToIso(date, minutes, timezone);
}

/**
 * Extract the wall-clock parts (year, month, day, hour, minute) that the given
 * UTC instant displays at, in the target timezone.
 */
export function extractWallParts(
  isoString: string,
  timezone: string,
): WallParts {
  const date = new Date(isoString);
  const formatter = cachedFormatter("en-US", {
    timeZone: timezone,
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    hour12: false,
  });
  const parts = formatter.formatToParts(date);
  const getPart = (type: string) =>
    parseInt(parts.find((p) => p.type === type)?.value ?? "0");
  const rawHour = getPart("hour");
  return {
    year: getPart("year"),
    month: getPart("month") - 1,
    day: getPart("day"),
    hour: rawHour === 24 ? 0 : rawHour,
    minute: getPart("minute"),
  };
}

/**
 * Convert wall-clock parts in `timezone` back to a UTC ISO string. Inverse of
 * `extractWallParts`. DST-safe via `localMinutesToIso`.
 */
export function wallPartsToIso(parts: WallParts, timezone: string): string {
  const dateStr = `${parts.year}-${String(parts.month + 1).padStart(2, "0")}-${String(
    parts.day,
  ).padStart(2, "0")}`;
  return localMinutesToIso(dateStr, parts.hour * 60 + parts.minute, timezone);
}

/** Get today's wall-clock parts in the target tz (calendar today there). */
export function todayWallParts(timezone: string): WallParts {
  return extractWallParts(new Date().toISOString(), timezone);
}

/**
 * Date-only formatter using the target tz. Pass `Intl`-supported locale and
 * IANA tz id.
 */
export function formatDateInTz(
  isoString: string,
  locale: string,
  timezone: string,
): string {
  return cachedFormatter(locale, {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(isoString));
}

/**
 * Date+time formatter. When `withSuffix` is true and the tz differs from the
 * browser's local tz, appends " · <tz>" after the time so admins looking at
 * a non-local event see which tz they're reading.
 */
export function formatDateTimeInTz(
  isoString: string,
  locale: string,
  timezone: string,
  clock: string,
  withSuffix?: boolean,
): string {
  const date = new Date(isoString);
  const dateStr = formatDateInTz(isoString, locale, timezone);
  const timeStr = cachedFormatter(locale, {
    timeZone: timezone,
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
  const suffix =
    withSuffix && timezone !== BROWSER_TZ() ? ` · ${timezone}` : "";
  return `${dateStr} (${timeStr} ${clock}${suffix})`;
}

/**
 * Legacy date formatter for slotting (weekday + short month + day + year, in
 * tz). Kept here for the slotting/utils re-export.
 */
export function formatDate(
  isoString: string,
  locale: string,
  timezone: string,
): string {
  return cachedFormatter(locale, {
    timeZone: timezone,
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(isoString));
}

/**
 * True when `tz` is a timezone `Intl` will actually accept.
 *
 * Event timezones are stored data, and a malformed one (a legacy `"GMT+2"`, an
 * import, a hand-edited document) makes every `Intl.DateTimeFormat` call throw
 * `RangeError` — mid-render, where the nearest boundary is the app root and the
 * user's unsaved work goes with it. Callers that format in an event's zone
 * should fall back to the viewer's zone rather than take that risk.
 */
export function isValidTimeZone(tz: string | undefined | null): boolean {
  if (!tz) return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/**
 * The zone to actually render in: the event's when Intl accepts it, the
 * viewer's otherwise.
 *
 * Every surface that formats in an event's zone needs this, and hand-rolling
 * `eventTimeZone || browserTimeZone()` at each one is how the guard ended up
 * present at two call sites and missing at four — with a `RangeError` mid-render
 * waiting at the unguarded ones.
 */
export function safeDisplayTimeZone(tz: string | undefined | null): string {
  return isValidTimeZone(tz) ? (tz as string) : BROWSER_TZ();
}
